// Actors for the targets of the other games of the night: star reflections,
// paper lanterns with a name, glass floats. Same interface as the sprite and
// 3D character actors (group, play, update, uniforms, scale, flip) so the pool
// view can treat every target alike.
import * as THREE from 'three';

function starTexture() {
  // A four-point glint: thin tapered rays and a small core, so it reads as a star, not a lamp.
  const cv = document.createElement('canvas'); cv.width = cv.height = 128;
  const ctx = cv.getContext('2d');
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 22);
  g.addColorStop(0, 'rgba(255,250,225,1)'); g.addColorStop(0.35, 'rgba(255,225,150,0.55)'); g.addColorStop(1, 'rgba(255,200,90,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
  ctx.fillStyle = 'rgba(255,248,220,0.95)';
  for (let k = 0; k < 4; k++) {
    const a = k * Math.PI / 2, L = 60, W = 5;
    ctx.beginPath(); ctx.moveTo(64 + Math.cos(a) * L, 64 + Math.sin(a) * L);
    ctx.lineTo(64 + Math.cos(a + Math.PI / 2) * W, 64 + Math.sin(a + Math.PI / 2) * W);
    ctx.lineTo(64 - Math.cos(a) * 4, 64 - Math.sin(a) * 4);
    ctx.lineTo(64 + Math.cos(a - Math.PI / 2) * W, 64 + Math.sin(a - Math.PI / 2) * W);
    ctx.closePath(); ctx.fill();
  }
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t;
}
let STAR_TEX = null;

function labelSprite(text) {
  const cv = document.createElement('canvas'); cv.width = 256; cv.height = 64;
  const ctx = cv.getContext('2d');
  ctx.font = `36px ${getComputedStyle(document.body).getPropertyValue('--serif').trim() || '"Shippori Mincho", serif'}`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(10,8,14,0.55)';
  const w = Math.min(250, ctx.measureText(text).width + 28);
  ctx.fillRect(128 - w / 2, 10, w, 44);
  ctx.fillStyle = '#ffe2b0'; ctx.fillText(text, 128, 33);
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, transparent: true, depthTest: false }));
  s.scale.set(1.6, 0.4, 1); s.renderOrder = 9;
  return s;
}

export class TargetActor {
  constructor(kind, { kagee = false, name = '' } = {}) {
    this.kind = kind;
    this.uniforms = { glow: { value: new THREE.Color(0, 0, 0) }, opacity: { value: 1 }, dim: { value: 0 }, light: { value: new THREE.Color(1, 1, 1) } };
    this.group = new THREE.Group();
    this.holder = new THREE.Group();
    this.group.add(this.holder);
    this.mesh = this.holder;
    this.scale = new THREE.Vector2(1, 1);
    this.t = Math.random() * 6;
    this.anim = 'idle';
    if (kind === 'star') {
      STAR_TEX ||= starTexture();
      this.sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: STAR_TEX, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: new THREE.Color(1.6, 1.4, 1.0) }));
      this.sprite.scale.set(0.9, 0.9, 1);
      this.sprite.position.y = 0.12;
      this.sprite.renderOrder = 4;
      this.holder.add(this.sprite);
    } else if (kind === 'lantern') {
      const paper = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.42, 10), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.7, 0.95, 0.45) }));
      paper.position.y = 0.32;
      const black = new THREE.MeshBasicMaterial({ color: 0x0a0806 });
      const top = new THREE.Mesh(new THREE.CylinderGeometry(0.27, 0.27, 0.06, 10), black); top.position.y = 0.55;
      const bot = new THREE.Mesh(new THREE.CylinderGeometry(0.27, 0.27, 0.06, 10), black); bot.position.y = 0.09;
      const raft = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.05, 0.62), kagee ? black : new THREE.MeshStandardMaterial({ color: 0x6a4a34 }));
      raft.position.y = 0.03;
      this.holder.add(paper, top, bot, raft);
      this.paper = paper;
      if (name) { this.label = labelSprite(name); this.label.position.y = 1.15; this.group.add(this.label); }
      this.light = new THREE.PointLight(0xffa850, 1.6, 3.5, 1.8);
      this.light.position.y = 0.4;
      this.group.add(this.light);
    } else if (kind === 'float') {
      const glass = new THREE.Mesh(new THREE.SphereGeometry(0.34, 18, 12), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.0, 0.55, 0.14), transparent: true, opacity: 0.92 }));
      glass.position.y = 0.2;
      const net = new THREE.Mesh(new THREE.SphereGeometry(0.355, 8, 6), new THREE.MeshBasicMaterial({ color: 0x0b0910, wireframe: true }));
      net.position.y = 0.2;
      this.holder.add(glass, net);
      this.glass = glass;
    }
  }
  has(name) { return ['idle', 'bump', 'saved', 'taken'].includes(name); }
  play(name) { this.anim = name; this.at = 0; }
  hold() {}
  release() {}
  set flip(v) {}

  update(dt, camera) {
    this.t += dt;
    const k = this.uniforms.opacity.value;
    if (this.kind === 'star') {
      const tw = 0.82 + 0.18 * Math.sin(this.t * 3.1) * Math.sin(this.t * 1.7);
      this.sprite.scale.setScalar(0.9 * tw * (this.anim === 'saved' ? 1.4 : 1));
      this.sprite.material.opacity = k * (1 - this.uniforms.dim.value * 0.85);
    } else if (this.kind === 'lantern') {
      this.holder.position.y = Math.sin(this.t * 1.6) * 0.03;
      this.holder.rotation.z = Math.sin(this.t * 1.1) * 0.05;
      const flick = 0.9 + 0.1 * Math.sin(this.t * 9) * Math.sin(this.t * 5.3);
      this.paper.material.color.setRGB(1.7 * flick, 0.95 * flick, 0.45 * flick).multiplyScalar(1 - this.uniforms.dim.value * 0.8);
      this.light.intensity = 1.6 * flick * k * (1 - this.uniforms.dim.value);
      if (this.label) this.label.material.opacity = k;
    } else if (this.kind === 'float') {
      this.holder.position.y = Math.sin(this.t * 1.4) * 0.025;
      this.glass.material.opacity = 0.92 * k;
      this.glass.material.color.setRGB(1.0, 0.55, 0.14).multiplyScalar(1 - this.uniforms.dim.value * 0.7).add(this.uniforms.glow.value);
    }
    this.holder.scale.set(this.scale.x, this.scale.y, this.scale.x);
    this.holder.position.x = this.offsetX || 0;
    this.group.visible = k > 0.01;
  }
  dispose() { this.group.traverse(o => { if (o.isMesh || o.isSprite) { o.geometry?.dispose(); o.material.dispose(); } }); }
}
